import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect } from "react";
import { pick, useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { Timer } from "../ui/Timer.js";
import { navigate } from "../router.js";
import { useStore } from "../store/state.js";
import { BundleList } from "../store/cards.js";
import { useEvents } from "./state.js";
import { dailyApi } from "./api.js";
import { Milestones, sourceText } from "./PlazaView.js";
import type { MissionView } from "./types.js";

export function MissionRow({ m, testId }: { m: MissionView; testId: string }) {
  const t = useT();
  const pct = Math.min(100, Math.round((m.progress / m.target) * 100));
  return (
    <div className={`mission ${m.done ? "done" : ""}`} data-testid={testId}>
      <div className="row">
        <span className="grow ellipsis">{pick(m.name)}</span>
        <span className="mono dim" style={{ fontSize: 11 }}>
          {Math.min(m.progress, m.target)}/{m.target}
        </span>
        <span className="badge gold mono">+{m.points}</span>
      </div>
      <div className="progress" style={{ marginTop: 4 }}>
        <div className={`progress-fill ${m.done ? "gold" : ""}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="dim" style={{ fontSize: 10 }}>{sourceText(m.source, t)}</div>
    </div>
  );
}

/** The daily loop: login calendar, missions with activity chests, Dawn Protocol, intel missions (R60). */
export function DailyView() {
  const t = useT();
  const daily = useEvents((s) => s.daily);
  const refreshDaily = useEvents((s) => s.refreshDaily);
  const refreshRuns = useEvents((s) => s.refreshRuns);
  const run = useEvents((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  useEffect(() => {
    void refreshDaily();
  }, [refreshDaily]);
  if (!daily) return <p className="hint">{t("common.loading")}</p>;
  const reload = () => {
    void refreshDaily();
    void refreshRuns();
  };
  const claimLogin = async () => {
    const res = await run(() => dailyApi.claimLogin(), t("events.toast.login"));
    if (res) {
      celebrate({ key: "login", granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      reload();
    }
  };
  const claimDay28 = async () => {
    const res = await run(() => dailyApi.claimDay28(), t("events.toast.day28"));
    if (res) {
      celebrate({ key: "day28", granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      reload();
    }
  };
  const startIntel = async (id: string) => {
    if (await run(() => dailyApi.startIntel(id), t("events.toast.intelStarted"))) reload();
  };
  const claimIntel = async (id: string) => {
    const res = await run(() => dailyApi.claimIntel(id), t("events.toast.intelClaimed"));
    if (res) {
      celebrate({ key: id, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      reload();
    }
  };
  const done = daily.missions.filter((m) => m.done).length;
  return (
    <div data-testid="daily-view">
      <Panel title={t("events.daily.login")} actions={<span className="mono dim"><FunctionalIcon name="clock" /> <Timer finishesAt={daily.resetsAt} /></span>}>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <span className="badge">{t("events.daily.streak", { n: daily.streak })}</span>
          <span className="grow" />
          {daily.login.claimable ? (
            <Button variant="gold" onClick={() => void claimLogin()} data-testid="login-claim">
              {t("events.daily.claimDay", { n: daily.login.index + 1 })}
            </Button>
          ) : (
            <span className="dim">{t("events.daily.claimed")}</span>
          )}
        </div>
        <div className="calendar-strip">
          {daily.login.calendar.map((b, i) => (
            <div key={i} className={`cal-day ${i === daily.login.index ? "today" : ""} ${i < daily.login.index ? "past" : ""}`} data-testid={`cal-day-${i}`}>
              <div className="mono dim" style={{ fontSize: 11 }}>{t("events.daily.day", { n: i + 1 })}</div>
              <BundleList bundle={b} compact />
            </div>
          ))}
        </div>
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <span className="dim" style={{ fontSize: 12 }}>{t("events.daily.day28", { day: daily.login.day28.day })}</span>
          <span className="grow" />
          <Button variant={daily.login.day28.claimable ? "gold" : "ghost"} disabled={!daily.login.day28.claimable} onClick={() => void claimDay28()} data-testid="day28-claim">
            {t("common.claim")}
          </Button>
        </div>
      </Panel>

      <Panel title={t("events.daily.missions", { done, total: daily.missions.length })}>
        {daily.activity.runId ? <Milestones runId={daily.activity.runId} milestones={daily.activity.chests} points={daily.activity.points} onChange={reload} testPrefix="activity" /> : null}
        <div className="missions" data-testid="daily-missions">
          {daily.missions.map((m) => (
            <MissionRow key={m.id} m={m} testId={`mission-${m.id}`} />
          ))}
        </div>
      </Panel>

      <Panel title={t("events.daily.dawn")}>
        <p className="hint">{t("events.daily.dawnHint")}</p>
        {daily.dawn.runId ? <Milestones runId={daily.dawn.runId} milestones={daily.dawn.milestones} points={daily.dawn.points} onChange={reload} testPrefix="dawn" /> : null}
      </Panel>

      <Panel title={t("events.daily.intel")} actions={daily.intel.nextBatchAt ? <span className="mono dim"><FunctionalIcon name="clock" /> <Timer finishesAt={daily.intel.nextBatchAt} /></span> : null}>
        <p className="hint">{t("events.daily.intelHint", { n: daily.intel.storage })}</p>
        <div className="offers" data-testid="intel-list">
          {daily.intel.missions.map((m) => (
            <article key={m.id} className="offer" data-testid={`intel-${m.id}`}>
              <div className="offer-name">{pick(m.name)}</div>
              <div className="dim" style={{ fontSize: 11 }}>{t("events.daily.intelMinutes", { n: m.minutes })}</div>
              <BundleList bundle={m.reward} compact />
              <div className="row" style={{ marginTop: 6 }}>
                <span className="grow" />
                {m.claimable ? (
                  <Button variant="gold" onClick={() => void claimIntel(m.id)} data-testid={`intel-claim-${m.id}`}>
                    {t("common.claim")}
                  </Button>
                ) : m.doneAt ? (
                  <span className="mono dim">
                    <Timer finishesAt={m.doneAt} onDone={reload} />
                  </span>
                ) : (
                  <Button onClick={() => void startIntel(m.id)} data-testid={`intel-start-${m.id}`}>
                    {t("events.daily.intelStart")}
                  </Button>
                )}
              </div>
            </article>
          ))}
        </div>
      </Panel>

      <Panel title={t("events.pass.missions")} actions={<Button variant="ghost" onClick={() => navigate("/app/events/pass")}>{t("events.tab.pass")}</Button>}>
        <div className="missions">
          {daily.pass.daily.map((m) => (
            <MissionRow key={m.id} m={m} testId={`pass-mission-${m.id}`} />
          ))}
        </div>
        <div className="section">
          <h3>{t("events.pass.weekly")}</h3>
          <div className="missions">
            {daily.pass.weekly.map((m) => (
              <MissionRow key={m.id} m={m} testId={`pass-mission-${m.id}`} />
            ))}
          </div>
        </div>
      </Panel>
    </div>
  );
}
